import { ListeningBars } from '$tablet-app/src/app/components/ListeningBars/ListeningBars';
import Row from '$tablet-app/src/app/components/Row';
import { Button } from '@akin-home/buttons/native';
import { ChatResponseOption } from '@akin-home/chat';
import { Text } from '@akin-home/text/native';
import { colors, font_variant_styles } from '@akin-home/theme';
import { UserModel } from '@akin-home/user';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { ReactNode } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import styled from 'styled-components/native';
import { ChatItem } from '../../../ChatItem/types';
import { ChatItemsView } from './ChatItemsView';

type Props = {
  user: UserModel;

  state: FocusedChatWindowState;

  avatar?: ReactNode;

  // Input Changes
  onChangeInput?(text: string): void;

  // Button Presses
  onPressMicrophone?(): void;
  onPressClose?(): void;
  onPressSend?(): void;
  onPressOption?(
    option: ChatResponseOption,
    index: number,
    context?: any
  ): void;

  onPressDone?(message: string, index: number): void;
  onPressSkip?(message: string, index: number): void;
};

export type FocusedChatWindowState = {
  input: string;
  chat_items?: ChatItem[];
  microphone_active?: boolean;
};

/** Handles the visual component of the focused chat window */
export function FocusedChatWindowView(props: Props) {
  const {
    user,
    state: { microphone_active },
  } = props;

  const onEnterKey = (
    event: NativeSyntheticEvent<TextInputKeyPressEventData>
  ) => {
    if (event.nativeEvent.key === 'Enter') {
      // Prevent default so the input does not lose focus
      event.preventDefault();
      props.onPressSend?.();
    }
  };

  return (
    <Backdrop>
      <Container>
        <LeftContainer>
          <Text weight="semibold">Hi, {user.name}</Text>
          <Text>How can I help you?</Text>

          <LeftMain>{props.avatar}</LeftMain>

          <Row justifyContent="center">
            {microphone_active ? <ListeningBars /> : null}
          </Row>
        </LeftContainer>

        <RightContainer>
          <TopRight>
            <Button
              variant="secondary"
              icon="times"
              onPress={props.onPressClose}
            />
          </TopRight>

          <ChatArea>
            {props.state.chat_items ? (
              <ChatItemsView
                items={props.state.chat_items}
                onPressOption={props.onPressOption}
              />
            ) : null}
          </ChatArea>

          <BottomRight>
            <MicrophoneCircle
              onPress={props.onPressMicrophone}
              style={{
                backgroundColor: microphone_active
                  ? colors.success[60]
                  : colors.neutral[10],
              }}
            >
              <FontAwesomeIcon
                icon="microphone"
                size={21}
                color={microphone_active ? 'white' : undefined}
              />
            </MicrophoneCircle>

            <Input
              placeholder="Chat with Pixi"
              value={props.state.input}
              onChangeText={props.onChangeInput}
              onKeyPress={onEnterKey as any}
            />

            <SubmitButton size="large" onPress={props.onPressSend}>
              <Text color="white" weight="semibold">
                Send
              </Text>
              <FontAwesomeIcon icon="paper-plane" color="white" size={21} />
            </SubmitButton>
          </BottomRight>
        </RightContainer>
      </Container>
    </Backdrop>
  );
}

const Backdrop = styled.View`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  padding: 24px;

  background-color: ${colors.grey[200]};
`;

const Container = styled.View`
  flex: 1;
  border-radius: 20px;
  background-color: white;

  flex-direction: row;
`;

const LeftContainer = styled.View`
  padding: 20px;
  width: 460px;

  border-right-width: 1px;
  border-right-color: ${colors.neutral[10]};

  justify-content: space-between;
`;

const LeftMain = styled.View`
  flex: 1;

  align-items: center;
  justify-content: center;
`;

const RightContainer = styled.View`
  flex: 1;
  padding: 30px;
`;

const TopRight = styled.View`
  flex-direction: row;
  justify-content: flex-end;
`;

const BottomRight = styled.View`
  flex-direction: row;
  gap: 16px;
  align-items: center;
`;

const MicrophoneCircle = styled.TouchableOpacity`
  height: 53px;
  width: 53px;
  border-radius: 100px;

  align-items: center;
  justify-content: center;
`;

const Input = styled.TextInput`
  background-color: ${colors.grey[200]};
  flex: 1;
  border-radius: 20px;
  padding: 16px 0 16px 24px;

  ${font_variant_styles.p.regular}
`;

const ChatArea = styled.View`
  flex: 1;
`;

const SubmitButton = styled(Button)`
  gap: 10px;
`;

const BarsContainer = styled.View``;
